import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, ExternalLink } from 'lucide-react';

export default function Footer() {
  const currentYear = 2026;

  const footerLinks = {
    platform: [
      { name: 'Features', href: '/features' },
      { name: 'Documentation', href: '/docs' },
      { name: 'API Reference', href: '/api' },
      { name: 'Roadmap', href: '/roadmap' },
    ],
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Careers', href: '/careers' },
      { name: 'Blog', href: '/blog' },
      { name: 'Press Kit', href: '/press' },
      { name: 'Contact', href: '/contact' },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Security', href: '/security' },
      { name: 'Compliance', href: '/compliance' },
      { name: 'GDPR', href: '/gdpr' },
    ],
    resources: [
      { name: 'Help Center', href: '/help' },
      { name: 'Community', href: '/community' },
      { name: 'Status', href: '/status' },
      { name: 'Changelog', href: '/changelog' },
      { name: 'Partners', href: '/partners' },
    ],
  };

  return (
    <footer className="relative z-10 bg-cao-darker border-t border-blue-500/10">
      {/* Animated top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
                <div className="relative w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden">
                  <img src="/CAO-Icon.svg" alt="CAO Platform" className="w-full h-full object-contain" />
                </div>
              </div>
              <div>
                <span className="text-xl font-bold text-white">CAO Platform</span>
                <span className="block text-xs text-slate-500">by NeuralQuantum.ai</span>
              </div>
            </Link>

            <p className="text-slate-400 text-sm mb-6 max-w-sm">
              Executive-level governance for autonomous AI systems. Ensuring AI behaves safely,
              explainably, and in alignment with human intent.
            </p>

            {/* Contact Link */}
            <div className="flex gap-4">
              <a
                href="mailto:contact@neuralquantum.ai"
                aria-label="Email"
                className="w-10 h-10 rounded-lg bg-slate-800/50 border border-slate-700/50 flex items-center justify-center text-slate-400 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/10 transition-all duration-300"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Platform</h3>
            <ul className="space-y-3">
              {footerLinks.platform.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-blue-400 transition-colors animated-underline"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-blue-400 transition-colors animated-underline"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-blue-400 transition-colors animated-underline"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Resources</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-blue-400 transition-colors animated-underline"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Compliance Readiness Badges */}
        <div className="border-t border-slate-800 pt-8 mb-8">
          <p className="text-center text-xs text-slate-500 mb-4">
            Built to meet enterprise compliance standards — certification in progress
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {[
              { name: 'SOC 2', subtitle: 'Type II Ready' },
              { name: 'ISO 27001', subtitle: 'Ready' },
              { name: 'HIPAA', subtitle: 'Ready' },
              { name: 'GDPR', subtitle: 'Ready' },
              { name: 'Zero Trust', subtitle: 'Architecture' },
            ].map((badge, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/30 border border-slate-700/50"
              >
                <Shield className="w-4 h-4 text-blue-400" />
                <div>
                  <span className="text-xs font-semibold text-white">{badge.name}</span>
                  <span className="text-xs text-slate-500 ml-1">{badge.subtitle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trademark & Patent Notice */}
        <div className="border-t border-slate-800 pt-8 mb-8">
          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
            <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-400" />
              Intellectual Property & Legal Notices
            </h4>

            <div className="space-y-4 text-xs text-slate-400 leading-relaxed">
              <p>
                <strong className="text-slate-300">Trademark Notice:</strong> Chief Agentic Officer™, CAO™, CAO Platform™,
                CAOPlatform.com™, Nellie Ross™, Executive Council™, Five-Tier Autonomy Model™, VibeCaaS™, and NeuralQuantum™
                are trademarks or registered trademarks of NeuralQuantum.ai LLC in the United States and other countries.
                The CAO logo, Nellie Ross persona, and all associated visual identities are protected trademarks.
                All other trademarks, service marks, and trade names referenced herein are the property of their
                respective owners. Unauthorized use of any NeuralQuantum.ai LLC trademarks is strictly prohibited.
              </p>

              <p>
                <strong className="text-slate-300">Patent Notice:</strong> The CAO Platform™ and its associated
                technologies, including but not limited to the Five-Tier Autonomy Model™, Executive Council
                Architecture, Distributed Governance Framework, AI Behavioral Alignment Systems, and Multi-Agent
                Consensus Protocols, are protected by one or more patents and/or pending patent applications in
                the United States and internationally. Patent pending applications include methods for autonomous
                AI governance, multi-agent behavioral oversight systems, human-in-the-loop enforcement mechanisms,
                graduated autonomy classification systems, and real-time AI decision auditing frameworks.
                Covered by U.S. Patent Pending. International patents pending.
              </p>

              <p>
                <strong className="text-slate-300">Copyright Notice:</strong> © 2024-2026 NeuralQuantum.ai LLC.
                All content, documentation, software, source code, algorithms, designs, graphics, user interfaces,
                the Nellie Ross digital persona, training methodologies, governance frameworks, and all other materials
                on this platform and associated services are protected by copyright and other intellectual property laws.
                Unauthorized reproduction, distribution, modification, reverse engineering, or use of any materials
                without express written permission from NeuralQuantum.ai LLC is strictly prohibited and may result
                in civil and criminal penalties.
              </p>

              <p>
                <strong className="text-slate-300">Proprietary Technology:</strong> The CAO Platform™ incorporates
                proprietary artificial intelligence technologies, governance algorithms, and behavioral constraint
                systems developed exclusively by NeuralQuantum.ai LLC. These technologies represent significant
                trade secrets and confidential business information protected under applicable trade secret laws.
              </p>

              <p>
                <strong className="text-slate-300">Digital Human Disclosure:</strong> Nellie Ross™ is a
                synthetic digital human persona created for AI governance purposes. Nellie Ross is not
                a real person and is powered by advanced AI models. The Nellie Ross character, persona,
                likeness, and associated intellectual property are owned exclusively by NeuralQuantum.ai LLC.
                Any resemblance to actual persons, living or deceased, is purely coincidental.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            {/* Copyright */}
            <div className="text-center lg:text-left">
              <p className="text-sm text-slate-400">
                © {currentYear} Chief Agentic Officer Platform — Executive-Level Governance for Autonomous AI Systems
              </p>
              <p className="text-xs text-slate-500 mt-1">
                <a
                  href="https://caoplatform.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  CAOPlatform.com
                </a>
                {' '}powered by{' '}
                <a
                  href="https://vibecaas.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  VibeCaaS.com
                </a>
                {' '}a division of{' '}
                <a
                  href="https://neuralquantum.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  NeuralQuantum.ai LLC
                </a>
                . All rights reserved.
              </p>
            </div>

            {/* Additional Links */}
            <div className="flex flex-wrap justify-center gap-6 text-xs text-slate-500">
              <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms</Link>
              <Link to="/cookies" className="hover:text-slate-300 transition-colors">Cookies</Link>
              <Link to="/accessibility" className="hover:text-slate-300 transition-colors">Accessibility</Link>
              <Link to="/sitemap" className="hover:text-slate-300 transition-colors">Sitemap</Link>
            </div>
          </div>

          {/* Final Legal Line */}
          <div className="mt-6 pt-6 border-t border-slate-800/50 text-center">
            <p className="text-[10px] text-slate-600 max-w-4xl mx-auto">
              This website and all associated services are provided "as is" without warranty of any kind,
              express or implied. NeuralQuantum.ai LLC reserves the right to modify, suspend, or discontinue
              any aspect of its services at any time without prior notice. Use of this platform constitutes
              acceptance of our Terms of Service and Privacy Policy. For inquiries regarding licensing,
              patents, or intellectual property, please contact legal@neuralquantum.ai.
            </p>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />
    </footer>
  );
}
